import type { AxiosInstance } from 'axios';
import axios from 'axios';
import qs from 'qs';

import type { BasePollingService, LbOptions, LbPost, LbUpdate } from '../types';
import logger from '../utils/logger';
import { hideFromWeb, silentCheckVar, postProcessPost, postProcessUpdate } from '../utils/mapping';

const paramsSerializer = (params: Record<string, any>) =>
  qs.stringify(params, {
    arrayFormat: 'comma',
    skipNulls: true,
    sort: (a, b) => a.localeCompare(b),
  });

export class PollingService implements BasePollingService {
  options: LbOptions;
  client: AxiosInstance;

  constructor(options: LbOptions) {
    this.options = options;

    this.client = axios.create({
      baseURL: `${this.options.host}${this.options.basePath}`,
      headers: this.options.headers,
      paramsSerializer,
    });
    this.client.interceptors.response.use(null, logger.error);
  }

  callCache = new Map<string, WeakRef<Promise<any>>>();

  executeFetch = (url: string, params: Record<string, any>) => {
    if (!this.options.blogId) return null;

    url = url.replace('{blogId}', this.options.blogId);

    const key = this.client.getUri({ url, params: qs.parse(paramsSerializer(params)) });
    if (this.callCache.has(key)) {
      return this.callCache.get(key).deref();
    }

    const request = this.client
      .get(url, { params, headers: this.options.headers })
      .then((response) => response?.data)
      .finally(() => this.callCache.delete(key));

    this.callCache.set(key, new WeakRef(request));

    return request;
  };

  /**
   * Gets a page of posts
   */
  async loadPosts(lastPostId?: string) {
    let result;
    try {
      const qs = {
        includeReactions: this.options.includeReactions,
        aggregator: 'lightliveblogjson',
        limit: this.options.limit,
      };
      if (lastPostId) qs['from'] = lastPostId;

      result = await this.executeFetch(this.options.urls.posts, qs);
    } catch (error) {
      logger.error(error);
    }

    let items = result?.result ?? [];
    let version: string;
    if (items) {
      if (!result?.version || version !== result.version) {
        version = result?.version;
      }
    } else {
      version = null;
    }

    if (this.options.transport?.filter) {
      items = items.filter(this.options.transport.filter);
    }

    items = items
      .filter(Boolean)
      .map(postProcessPost(this.options.baseImgUrl))
      .filter((x: LbPost) => !hideFromWeb(x))
      .filter((x: LbPost) => !silentCheckVar(x));

    return { items, version };
  }

  /**
   * Updates the posts from the first request
   */
  async getUpdates(perviousVersion: string) {
    if (!perviousVersion) {
      return;
    }

    if (!this.options.urls.events) {
      logger.error('Events url is null');
      return;
    }

    // First flow
    const qs = {
      includeReactions: this.options.includeReactions,
      version: perviousVersion,
      aggregator: 'lightliveblogjson',
    };

    let result;
    try {
      result = await this.executeFetch(this.options.urls.events, qs);
    } catch (error) {
      logger.error(error);
    }

    if (result?.version && perviousVersion === result.version) {
      return;
    }

    let items = result?.result ?? [];
    if (this.options.transport?.filter) {
      items = items.filter(this.options.transport.filter);
    }
    items = items
      .filter(Boolean)
      .map(postProcessUpdate(this.options.baseImgUrl))
      .filter((x: LbUpdate) => !hideFromWeb(x.body))
      .filter((x: LbUpdate) => !silentCheckVar(x.body));

    return { items, version: result?.version };
  }
}
